import { createRoute, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import rootRoute from '../rootRoute'
import AdminTabs from './AdminTabs'
import { $api } from '@/lib/fetch'
import { adminRouteLoader } from '@/lib/auth'
import { Badge, Button, Row, spacing } from '@/components/ui'
import { catOf } from '@/lib/types'
import { EventStatus } from '@/lib/constants'
import { prettyDate } from '@/lib/helpers'

const STATUS_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: EventStatus.Verified, label: 'Vérifiés' },
  { value: EventStatus.NotVerified, label: 'En attente' },
  { value: EventStatus.SoftDelete, label: 'Supprimés' },
]

const STATUS_TONES: Record<string, 'emerald' | 'gold' | 'terracotta'> = {
  [EventStatus.Verified]: 'emerald',
  [EventStatus.NotVerified]: 'gold',
  [EventStatus.SoftDelete]: 'terracotta',
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: '7px 14px',
    borderRadius: 'var(--radius-pill)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13.5,
    border: `1.5px solid ${active ? 'var(--emerald-700)' : 'var(--border-strong)'}`,
    background: active ? 'var(--emerald-700)' : 'var(--surface-card)',
    color: active ? '#fff' : 'var(--ink-1)',
    fontFamily: 'var(--font-body)',
  }
}

function AdminAllEventsView() {
  const navigate = useNavigate()
  const {
    status = '',
    type = '',
    last_id,
    last_ts,
  } = adminAllEventsRoute.useSearch()

  const pageToken =
    last_id != null && last_ts != null
      ? { last_id: last_id, last_start_timestamp_ms: last_ts }
      : undefined

  const { data: eventTypesData } = $api.useQuery('get', '/api/events/types')

  const { data, isLoading } = $api.useQuery(
    'post',
    '/api/events/admin-search',
    {
      body: {
        status: status ? (status as EventStatus) : undefined,
        types: type ? [type] : undefined,
        limit: 20,
        page_token: pageToken,
      },
    },
  )

  const events = data?.events ?? []
  const nextToken = data?.next_page_token

  const setFilter = (key: string, value: string) => {
    navigate({
      to: '/admin/all-events',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        [key]: value || undefined,
        last_id: undefined,
        last_ts: undefined,
      }),
    } as never)
  }

  const goNext = () => {
    if (!nextToken) return
    navigate({
      to: '/admin/all-events',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        last_id: nextToken.last_id,
        last_ts: nextToken.last_start_timestamp_ms,
      }),
    } as never)
  }

  const goPrev = () => window.history.back()

  return (
    <div
      style={{
        maxWidth: 'var(--container-max)',
        margin: '0 auto',
        padding: 'var(--space-8) var(--gutter) var(--space-20)',
      }}
    >
      <AdminTabs />
      <div className="oumma-overline" style={{ marginBottom: spacing.sm }}>
        Espace admin
      </div>
      <h1 style={{ fontSize: 'var(--text-h1)', margin: '0 0 24px' }}>
        Tous les événements
      </h1>

      <Row gap="sm" style={{ flexWrap: 'wrap', marginBottom: spacing.lg }}>
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            style={pillStyle(status === opt.value)}
            onClick={() => setFilter('status', opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </Row>

      <Row gap="sm" style={{ flexWrap: 'wrap', marginBottom: spacing.xl }}>
        <button
          style={pillStyle(type === '')}
          onClick={() => setFilter('type', '')}
        >
          Tous les types
        </button>
        {(eventTypesData?.types ?? []).map((et) => (
          <button
            key={et.type}
            style={pillStyle(type === et.type)}
            onClick={() => setFilter('type', type === et.type ? '' : et.type)}
          >
            {et.emoji} {et.type}
          </button>
        ))}
      </Row>

      {isLoading ? (
        <p style={{ color: 'var(--ink-2)' }}>Chargement…</p>
      ) : (
        <>
          <div className="oumma-table-wrap">
            <table className="oumma-table">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Type</th>
                  <th>Statut</th>
                  <th>Date</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        textAlign: 'center',
                        color: 'var(--ink-3)',
                        padding: 24,
                      }}
                    >
                      Aucun événement trouvé.
                    </td>
                  </tr>
                )}
                {events.map((e) => (
                  <tr
                    key={e.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      navigate({
                        to: '/events/$id/edit',
                        params: { id: String(e.id) },
                      })
                    }
                  >
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {e.title}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--ink-3)',
                          marginTop: 2,
                        }}
                      >
                        {e.location.address}
                      </div>
                    </td>
                    <td>
                      <Badge tone={catOf(e.type.type).tone}>
                        {e.type.emoji} {e.type.type}
                      </Badge>
                    </td>
                    <td>
                      <Badge tone={STATUS_TONES[e.status] ?? 'gold'}>
                        {e.status}
                      </Badge>
                    </td>
                    <td
                      style={{
                        fontSize: 13,
                        color: 'var(--ink-2)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {prettyDate(e.start_date)}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      {e.source === 'manual_source' ? 'Manuel' : e.source}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Row gap="md" justify="flex-end" style={{ marginTop: spacing.lg }}>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<ChevronLeft size={15} />}
              onClick={goPrev}
              disabled={last_id == null}
            >
              Précédent
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconRight={<ChevronRight size={15} />}
              onClick={goNext}
              disabled={!nextToken}
            >
              Suivant
            </Button>
          </Row>
        </>
      )}
    </div>
  )
}

export const adminAllEventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/all-events',
  validateSearch: (search: Record<string, unknown>) => ({
    status: (search.status as string | undefined) ?? '',
    type: (search.type as string | undefined) ?? '',
    last_id: search.last_id ? Number(search.last_id) : undefined,
    last_ts: search.last_ts ? Number(search.last_ts) : undefined,
  }),
  loader: adminRouteLoader,
}).lazy(() => import('./AdminAllEventsView.lazy').then((m) => m.Route))

export default AdminAllEventsView
